import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, useViewport } from '@xyflow/react';
import { cn } from '@/lib/utils';
import * as Tooltip from '@radix-ui/react-tooltip';
import { EntitreeNode } from '../../../typesV2';
import { useAtom, useAtomValue } from 'jotai';
import { isLoadingNavAtom, contentAtom, isStreamingMapAtom, mobileSidebar } from '../../../jotaiStore';
import LoadingSpinnerSmall from '../../../features/global/loading/LoadingSpinnerSmall';
import Link from 'next/link';
import { slugify } from '@/lib/map/slugify';
import { useParams } from 'next/navigation';
import { TooltipContent } from '../components/TooltipContent';
import { showNavigationWarningToast, dismissNavigationWarningToast } from '@/lib/toast';
import getContentLink from '@/lib/map/getContentLink';
import { Loader2 } from 'lucide-react';
const { Top, Bottom, Left, Right } = Position;

const EndNode = memo((node: EntitreeNode) => {
  const isStreamingMap = useAtomValue(isStreamingMapAtom);
  const { data } = node;
  const { isSpouse, isSibling } = data;
  const { x, y, zoom } = useViewport();
  const mapSlug = useParams().mapSlug;
  const [showMobileSidebar, setShowMobileSidebar] = useAtom(mobileSidebar);
  const [, setIsLoadingNav] = useAtom(isLoadingNavAtom);
  const [, setContentData] = useAtom(contentAtom);
  const { contentSlug } = useParams();

  useEffect(() => {
    if (!isStreamingMap) {
      dismissNavigationWarningToast();
    }
  }, [isStreamingMap]);

  if (isStreamingMap) {
    return <Loader2 className="w-12 h-12 text-colors-green animate-spin ml-28" />;
  }

  return (
    <Tooltip.Root delayDuration={100}>
      <Tooltip.Trigger
        asChild
        id={`whats-next-tooltip`}
        className="pointer-events-auto"
      >
        <Link 
          href={getContentLink(x, y, zoom, contentSlug as string, mapSlug as string, 'What\'s Next', true)}
          onClick={(e) => {
           if (isStreamingMap) {
             e.preventDefault();
             showNavigationWarningToast();
           } else {
             if (showMobileSidebar) setShowMobileSidebar(false);
             setIsLoadingNav(true);
             setContentData('');
           }
          }}
        >
          <div className="flex items-center justify-center w-full h-full py-4 border-2 rounded-lg bg-shades-baseLight border-colors-green">
            <div className={cn('nodrag ')}>
              {/* Target Handle */}
              <Handle type={'target'} position={Top} id={Top} />
              <div className={'text-4xl underline text-shades-text'}>
                {data.topicTitle}
              </div>
            </div>
          </div>
        </Link>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <TooltipContent
          title={'What\'s Next'}
          description="Explore where you can go from here"
          mapSlug={mapSlug as string}
          labelText=""
          side='right'
        />
      </Tooltip.Portal>
    </Tooltip.Root>
  );
});

EndNode.displayName = 'EndNode';

export default EndNode;
